import DOMPurify from 'dompurify';

/**
 * Decodes HTML entities (handles double-encoding) and returns a plain string.
 * Use this for places where React or d3 will set textContent
 * (e.g. {value}, d3.select(...).text(value)) and entities like &lt;
 * would otherwise show up as literal "&lt;" in the UI.
 *
 * Examples:
 *   "&amp;lt;35"      → "<35"
 *   "&#x27;"          → "'"
 *
 * @param {*} input  Possibly-encoded string from the API
 * @returns {string} Plain, fully-decoded text (NOT sanitized HTML)
 */
export function decodeEntities(input) {
  if (input == null) return '';
  let str = String(input);

  // Decode HTML entities repeatedly until the string is stable.
  // Handles double-encoded values like "&amp;lt;" → "&lt;" → "<".
  const decoder = document.createElement('textarea');
  let prev;
  let safety = 5; // hard cap to prevent infinite loops on adversarial input
  do {
    prev = str;
    decoder.innerHTML = str;
    str = decoder.value;
    safety--;
  } while (str !== prev && safety > 0);

  return str;
}

/**
 * Decodes HTML entities (handles double-encoding), then sanitizes the result
 * so it's safe for dangerouslySetInnerHTML.
 *
 * Examples:
 *   "woman&#x27;s"          →  "woman's"
 *   "&lt;sup&gt;a&lt;/sup&gt;"  →  "<sup>a</sup>"  (renders as superscript)
 *   "<script>alert(1)</script>" →  ""              (script stripped)
 *
 * @param {*} input  Possibly-encoded HTML string from the API
 * @returns {string} Sanitized HTML safe for dangerouslySetInnerHTML
 */
export function safeHtml(input) {
  if (input == null) return '';
  const decoded = decodeEntities(input);
  // Sanitize the decoded HTML to strip dangerous tags/attributes
  return DOMPurify.sanitize(decoded);
}
